import React from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { Column } from 'react-table'
import { TableColumn, Link, Box, TextBody } from '@ally/metronome-ui'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { DashboardView } from '../../CustomizeDashboard/types'
import { NoAprDebtPlan } from './MobileDebtPlan'
import ProgressCircle from './ProgressCircle'
import { FocusDebtAccount } from '../../types'
import { createDebtPlanLinkAriaLabel } from './utils'

export function forecastedPayoffDateString(progress: number): string {
  return progress === 1 ? 'MM/DD/YY' : 'MMM YYYY'
}

const StyledActionLink = styled(Link)`
  @media print {
    white-space: normal;
  }
`

// override metronome style when using size='xs'
export const NoUpperCaseTextBody = styled(TextBody)`
  text-transform: none;
`

interface GetColumnsProps {
  view: DashboardView
  isLgDown: boolean
}

export const getColumns = ({
  view,
  isLgDown,
}: GetColumnsProps): TableColumn<FocusDebtAccount>[] => {
  // istanbul ignore next
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown
  const columns: Record<
    string,
    Column<FocusDebtAccount> & { alignment: 'right' | 'left' }
  > = {
    accountName: {
      id: 'name',
      accessor: 'id',
      Header: 'Manage Debt',
      alignment: 'left',
      Cell: ({ row }): React.ReactNode => {
        return (
          <>
            <Link
              to="/debt-plan"
              text={row.original.name}
              withAllyTM="debtPlan-debtPlanAccount"
              aria-label={createDebtPlanLinkAriaLabel(
                row.original.name,
                row.original.partialAccountNumber,
              )}
            />
            {maskAccountNumber(row.original.partialAccountNumber, 'bullets')}
          </>
        )
      },
    },
    progress: {
      id: 'progress',
      accessor: 'progress',
      Header: 'Progress',
      alignment: 'right',
      Cell: ({ row }): React.ReactNode => {
        return <ProgressCircle progress={row.original.progress} />
      },
    },
    currentBalance: {
      id: 'Current Balance',
      accessor: 'currentBalance',
      Header: 'Current Balance',
      alignment: 'right',
      Cell: ({ row }): React.ReactNode => {
        const notPlaid = row.original.source !== 'PLAID'
        return (
          <Box display="flex" flexDirection="column">
            <TextBody tag="span" size="sm">
              {asCurrency(row.original.currentBalance)}
            </TextBody>
            {notPlaid && (
              <NoUpperCaseTextBody tag="span" size="xs">
                As of {dayjs(row.original.lastUpdated).format('MM/DD/YYYY')}
              </NoUpperCaseTextBody>
            )}
          </Box>
        )
      },
    },
    projectedPayoff: {
      id: 'Forecasted Payoff',
      accessor: 'forecastedPayoff',
      Header: ({ rows }): string => {
        // rows[0] should be fine since there should only ever be one row in this table
        return rows[0].original.progress === 1
          ? 'Payoff Date'
          : 'Projected Payoff'
      },
      alignment: 'right',
      Cell: ({ row }): React.ReactNode => {
        if (!row.original.forecastedPayoff) {
          return <NoAprDebtPlan />
        }

        return dayjs(row.original.forecastedPayoff).format(
          forecastedPayoffDateString(row.original.progress),
        )
      },
    },
    actions: {
      id: 'actions',
      Header: 'Actions',
      alignment: 'right',
      Cell: ({ row }): React.ReactNode => {
        // TODO [DEBT_PLAN] is this the same link location as the debt account name?
        return <StyledActionLink to="/debt-plan" text="Track Progress" />
      },
    },
  }

  const tileViewCols = [
    columns.accountName,
    columns.progress,
    columns.currentBalance,
    columns.projectedPayoff,
  ] as TableColumn<FocusDebtAccount>[]
  const defaultViewCols = [
    columns.accountName,
    columns.progress,
    columns.currentBalance,
    columns.projectedPayoff,
    columns.actions,
  ] as TableColumn<FocusDebtAccount>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
